import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Camera,
  CheckCircle,
  Heart,
  PaperPlaneTilt,
  Phone,
  User,
  WarningCircle,
} from '@phosphor-icons/react'
import KioskHeader from '../../components/KioskHeader/KioskHeader'
import useKioskAutoHome from '../../hooks/useKioskAutoHome'

export default function SupportUsPage() {
  useKioskAutoHome()
  const navigate = useNavigate()

  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [photo, setPhoto] = useState(null)
  const [cameraActive, setCameraActive] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [captureCountdown, setCaptureCountdown] = useState(0)

  const canSubmit = name.trim().length > 0 && phone.trim().length > 0 && Boolean(photo)

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    setCameraActive(false)
  }

  const startCamera = async () => {
    setCameraError('')

    try {
      stopCamera()

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }

      setCameraActive(true)
    } catch (error) {
      setCameraError('Unable to open the camera. Please allow camera permission and try again.')
      setCameraActive(false)
    }
  }

  const startCaptureCountdown = () => {
    if (!cameraActive || captureCountdown > 0) return

    setCaptureCountdown(3)

    let nextCount = 3
    const timer = window.setInterval(() => {
      nextCount -= 1

      if (nextCount <= 0) {
        window.clearInterval(timer)
        setCaptureCountdown(0)
        capturePhoto()
        return
      }

      setCaptureCountdown(nextCount)
    }, 1000)
  }

  const capturePhoto = () => {
    const video = videoRef.current
    const canvas = canvasRef.current

    if (!video || !canvas) return

    const width = video.videoWidth || 1280
    const height = video.videoHeight || 720

    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')

    // Keep the captured image exactly like the live selfie preview.
    context.translate(width, 0)
    context.scale(-1, 1)
    context.drawImage(video, 0, 0, width, height)

    const imageData = canvas.toDataURL('image/jpeg', 0.9)
    setPhoto(imageData)
    stopCamera()
  }

  const retakePhoto = () => {
    setCaptureCountdown(0)
    setPhoto(null)
    startCamera()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!canSubmit) return

    // TODO: connect to backend API
    // Send name, phone, message, photo, and media consent to the backend.

    stopCamera()
    setSubmitted(true)

    window.setTimeout(() => {
      navigate('/', { replace: true })
    }, 3500)
  }

  useEffect(() => {
    return () => stopCamera()
  }, [])

  return (
    <main className="kiosk-page">
      <div className="app-container kiosk-container">
        <KioskHeader title="Say Hi" subtitle="Share your message for the community leader" />

        <section className="kiosk-section-card support-us-card">
          {!submitted ? (
            <form onSubmit={handleSubmit}>
              <div className="kiosk-section-card__header">
                <Heart size={30} />
                <div>
                  <h1>Say Hi</h1>
                  <p>Visitors can post a message and capture a photo to support the community leader.</p>
                </div>
              </div>

              <div className="form-grid support-us-form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <User size={15} /> Name <strong>*</strong>
                  </label>
                  <input
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Phone size={15} /> Phone <strong>*</strong>
                  </label>
                  <input
                    className="form-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Your contact number"
                  />
                </div>

                <div className="form-group form-group--full">
                  <label className="form-label">Message (optional)</label>
                  <textarea
                    className="form-textarea"
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write your support message here..."
                  />
                </div>

                <div className="form-group form-group--full">
                  <label className="form-label">Image <strong>*</strong></label>

                  <div className={`support-us-live-camera ${photo ? 'has-photo' : ''}`}>
                    {!photo && (
                      <>
                        <video
                          ref={videoRef}
                          className={`support-us-live-camera__video ${cameraActive ? 'is-active' : ''}`}
                          playsInline
                          muted
                          autoPlay
                        />

                        {!cameraActive && (
                          <div className="support-us-live-camera__empty">
                            <Camera size={44} />
                            <strong>Live camera required</strong>
                            <span>Please capture a photo before submitting.</span>
                          </div>
                        )}
                      </>
                    )}

                    {captureCountdown > 0 && (
                      <div className="visitor-camera-countdown">
                        <span>{captureCountdown}</span>
                        <strong>Get ready</strong>
                      </div>
                    )}

                    {photo && (
                      <img className="support-us-live-camera__photo" src={photo} alt="Captured support" />
                    )}
                  </div>

                  {cameraError && (
                    <div className="support-us-camera-error">
                      <WarningCircle size={18} /> {cameraError}
                    </div>
                  )}

                  <canvas ref={canvasRef} hidden />

                  <div className="support-us-camera-actions">
                    {!photo && !cameraActive && (
                      <button type="button" className="btn btn--primary" onClick={startCamera}>
                        <Camera size={18} /> Open Camera
                      </button>
                    )}

                    {!photo && cameraActive && (
                      <button type="button" className="btn btn--primary" onClick={startCaptureCountdown} disabled={captureCountdown > 0}>
                        <Camera size={18} /> {captureCountdown > 0 ? `Capturing in ${captureCountdown}` : 'Capture Photo'}
                      </button>
                    )}

                    {photo && (
                      <button type="button" className="btn btn--ghost" onClick={retakePhoto}>
                        Retake Photo
                      </button>
                    )}
                  </div>
                </div>

                <div className="form-group form-group--full">
                  <div className="support-us-disclaimer">
                    <WarningCircle size={20} />
                    <p>
                      By submitting this form, you allow us to use your message and captured image for
                      social media, online media, publicity, and community-related communication.
                    </p>
                  </div>
                </div>
              </div>

              <div className="submit-ticket-actions">
                <button type="button" className="btn btn--ghost" onClick={() => navigate('/')}>
                  Back to Home
                </button>
                <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                  <PaperPlaneTilt size={16} /> Submit Message
                </button>
              </div>
            </form>
          ) : (
            <div className="kiosk-success-state">
              <CheckCircle size={58} />
              <h1>Thank you for your support.</h1>
              <p>Your message has been submitted. The kiosk will return to the home page shortly.</p>
            </div>
          )}
        </section>
      </div>
    </main>
  )
}
